import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  SimpleGrid,
  Badge,
  Avatar,
  Flex,
  Button,
  Image,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react';
import {
  Calendar as CalendarIcon,
  Users as UsersIcon,
  Heart as HeartIcon,
  Music as MusicIcon,
  Coffee as CoffeeIcon,
  Book as BookIcon,
  Utensils as UtensilsIcon,
  Clock as ClockIcon,
  MapPin as MapPinIcon,
  Star as StarIcon,
  ChevronRight as ChevronRightIcon,
  Award as AwardIcon,
  Zap as ZapIcon,
  Bookmark as BookmarkIcon,
} from 'lucide-react';

interface EventType {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  icon: any;
  color: string;
  participants: number;
  maxParticipants: number;
  isPopular?: boolean;
  isNew?: boolean;
  isSaved?: boolean;
  category: 'upcoming' | 'popular' | 'recommended';
}

interface MobileCultureWellnessProps {
  onNavigate: (path: string) => void;
  onSelectEvent: (eventId: string) => void;
}

const MobileCultureWellness: React.FC<MobileCultureWellnessProps> = ({ 
  onNavigate,
  onSelectEvent 
}) => {
  // Colors
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const secondaryTextColor = useColorModeValue('gray.500', 'gray.500');
  const methodistBlue = '#0066CC';
  const methodistPurple = '#805AD5';
  const methodistGreen = '#38A169';
  
  // Events data
  const events: EventType[] = [
    {
      id: 'book-journal',
      title: 'Book Journal Club',
      description: 'Join our monthly book club where we discuss literature related to healthcare and wellbeing.',
      date: 'Mar 15, 2025',
      time: '12:00 - 1:00 PM',
      location: 'Virtual Meeting',
      icon: BookIcon,
      color: methodistBlue,
      participants: 18,
      maxParticipants: 25,
      isPopular: true,
      category: 'upcoming'
    },
    {
      id: 'cookoff-session',
      title: 'Healthy Cookoff Session',
      description: 'Learn quick, nutritious recipes designed for busy healthcare professionals.',
      date: 'Mar 18, 2025',
      time: '5:30 - 7:00 PM',
      location: 'Methodist Cafeteria',
      icon: UtensilsIcon,
      color: methodistGreen,
      participants: 12,
      maxParticipants: 20,
      isNew: true,
      category: 'popular'
    },
    {
      id: 'salsa-dance',
      title: 'Salsa Dance Class',
      description: 'De-stress with our beginner-friendly salsa class. No experience needed!',
      date: 'Mar 20, 2025',
      time: '6:00 - 7:30 PM',
      location: 'Methodist Wellness Center',
      icon: MusicIcon,
      color: methodistPurple,
      participants: 14,
      maxParticipants: 24,
      category: 'recommended'
    },
    {
      id: 'coffee-chat',
      title: 'Coffee & Connect',
      description: 'Informal gathering to connect with colleagues across departments.',
      date: 'Mar 16, 2025',
      time: '9:00 - 10:00 AM',
      location: 'Methodist Café',
      icon: CoffeeIcon,
      color: methodistBlue,
      participants: 8,
      maxParticipants: 15,
      category: 'upcoming'
    },
    {
      id: 'wellness-walk',
      title: 'Wellness Walk',
      description: 'Join a guided 30-minute walk around the hospital campus.',
      date: 'Mar 14, 2025',
      time: '12:15 - 12:45 PM',
      location: 'Main Entrance',
      icon: HeartIcon,
      color: methodistGreen,
      participants: 22,
      maxParticipants: 30,
      isPopular: true,
      category: 'popular'
    },
    {
      id: 'mindfulness-workshop',
      title: 'Mindfulness Workshop',
      description: 'Learn practical mindfulness techniques for the clinical environment.',
      date: 'Mar 22, 2025',
      time: '2:00 - 3:30 PM',
      location: 'Conference Room B',
      icon: ZapIcon,
      color: methodistPurple,
      participants: 15,
      maxParticipants: 25,
      isNew: true,
      category: 'recommended'
    },
  ];

  // Filter events by category
  const upcomingEvents = events.filter(event => event.category === 'upcoming');
  const popularEvents = events.filter(event => event.category === 'popular' || event.isPopular);
  const recommendedEvents = events.filter(event => event.category === 'recommended');

  // Event card component
  const EventCard = ({ event }: { event: EventType }) => (
    <Box 
      bg={cardBg} 
      borderWidth="1px" 
      borderColor={borderColor} 
      borderRadius="lg" 
      overflow="hidden"
      shadow="sm"
      onClick={() => onSelectEvent(event.id)}
      cursor="pointer"
      transition="all 0.2s"
      _hover={{ transform: 'translateY(-2px)', shadow: 'md' }}
    >
      <Box position="relative">
        <Box 
          bg={`${event.color}15`} 
          p={4} 
          textAlign="center"
        >
          <Icon as={event.icon} boxSize={8} color={event.color} />
        </Box>
        
        {/* Badges */}
        <HStack position="absolute" top={2} right={2} spacing={1}>
          {event.isNew && (
            <Badge colorScheme="green" fontSize="xs" borderRadius="full">
              New
            </Badge>
          )}
          {event.isPopular && (
            <Badge colorScheme="red" fontSize="xs" borderRadius="full">
              Popular
            </Badge>
          )}
          {event.isSaved && (
            <Icon as={BookmarkIcon} boxSize={4} color="blue.500" />
          )}
        </HStack>
      </Box>

      <Box p={3}>
        <Text fontSize="sm" mb={1} noOfLines={1} fontWeight="medium">
          {event.title}
        </Text>
        
        <Text fontSize="xs" color={secondaryTextColor} mb={3} noOfLines={2}>
          {event.description}
        </Text>
        
        <HStack fontSize="xs" color={secondaryTextColor} mb={1} spacing={2}>
          <Icon as={CalendarIcon} boxSize={3} />
          <Text>{event.date}</Text>
        </HStack>
        
        <HStack fontSize="xs" color={secondaryTextColor} mb={1} spacing={2}>
          <Icon as={ClockIcon} boxSize={3} />
          <Text>{event.time}</Text>
        </HStack>
        
        <HStack fontSize="xs" color={secondaryTextColor} mb={3} spacing={2}>
          <Icon as={MapPinIcon} boxSize={3} />
          <Text noOfLines={1}>{event.location}</Text>
        </HStack>
        
        <HStack justify="space-between" align="center">
          <HStack>
            <Avatar size="2xs" bg={methodistBlue} icon={<Icon as={UsersIcon} color="white" boxSize={2} />} />
            <Text fontSize="xs">
              {event.participants}/{event.maxParticipants} joined
            </Text>
          </HStack>
          <Icon as={ChevronRightIcon} boxSize={4} color="gray.400" />
        </HStack>
      </Box>
    </Box>
  );

  return (
    <Box pt={12} pb={6}>
      {/* Header */}
      <Box px={4} mb={6}>
        <Text fontSize="xl" fontWeight="bold" color={methodistBlue} mb={1}>
          Culture of Wellness
        </Text>
        <Text fontSize="sm" color={secondaryTextColor}>
          Connect, grow, and thrive with your colleagues
        </Text>
      </Box>

      {/* Wellness Stats */}
      <Box px={4} mb={6}>
        <SimpleGrid columns={3} spacing={3}>
          <Box bg={cardBg} p={3} borderRadius="lg" shadow="sm" borderWidth="1px" borderColor={borderColor}>
            <Icon as={CalendarIcon} color={methodistBlue} boxSize={5} mb={1} />
            <Text fontSize="lg" fontWeight="bold">12</Text>
            <Text fontSize="xs" color={secondaryTextColor}>Events This Month</Text>
          </Box>
          <Box bg={cardBg} p={3} borderRadius="lg" shadow="sm" borderWidth="1px" borderColor={borderColor}>
            <Icon as={UsersIcon} color={methodistPurple} boxSize={5} mb={1} />
            <Text fontSize="lg" fontWeight="bold">86</Text>
            <Text fontSize="xs" color={secondaryTextColor}>Participants</Text>
          </Box>
          <Box bg={cardBg} p={3} borderRadius="lg" shadow="sm" borderWidth="1px" borderColor={borderColor}>
            <Icon as={AwardIcon} color={methodistGreen} boxSize={5} mb={1} />
            <Text fontSize="lg" fontWeight="bold">4</Text>
            <Text fontSize="xs" color={secondaryTextColor}>Your Events</Text>
          </Box>
        </SimpleGrid>
      </Box>

      {/* Upcoming Events */}
      <Box px={4} mb={6}>
        <HStack justify="space-between" mb={3}>
          <Text fontSize="md" fontWeight="medium">Upcoming Events</Text>
          <Text 
            fontSize="sm" 
            color={methodistBlue} 
            cursor="pointer"
            onClick={() => onNavigate('all-events')}
          >
            See All
          </Text>
        </HStack>
        <VStack spacing={3} align="stretch">
          {upcomingEvents.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </VStack>
      </Box>

      {/* Popular Events */}
      <Box px={4} mb={6}>
        <Text fontSize="md" fontWeight="medium" mb={3}>Popular Events</Text>
        <SimpleGrid columns={2} spacing={3}>
          {popularEvents.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </SimpleGrid>
      </Box>

      {/* Recommended For You */}
      <Box px={4} mb={6}>
        <Text fontSize="md" fontWeight="medium" mb={3}>Recommended For You</Text>
        <SimpleGrid columns={2} spacing={3}>
          {recommendedEvents.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </SimpleGrid>
      </Box>

      {/* Create Event Button */}
      <Box px={4}>
        <Button 
          colorScheme="blue" 
          size="md" 
          width="full"
          leftIcon={<Icon as={CalendarIcon} />}
          onClick={() => onNavigate('create-event')}
        >
          Create Wellness Event
        </Button>
      </Box>

      {/* Divider with text */}
      <Box px={4} my={6} position="relative">
        <Divider />
        <Text 
          position="absolute" 
          top="50%" 
          left="50%" 
          transform="translate(-50%, -50%)" 
          bg={useColorModeValue('white', 'gray.800')} 
          px={4} 
          fontSize="xs" 
          color={secondaryTextColor}
        >
          SentientIQ Culture
        </Text>
      </Box>

      {/* Wellness Quote */}
      <Box px={4} textAlign="center">
        <Icon as={HeartIcon} color={methodistPurple} boxSize={5} mb={2} />
        <Text fontSize="sm" fontStyle="italic" color={textColor}>
          "Wellness is not a destination, but a path we walk together."
        </Text>
        <Text fontSize="xs" color={secondaryTextColor} mt={1}>
          Methodist Health System Wellness Initiative
        </Text>
      </Box>
    </Box>
  );
};

export default MobileCultureWellness;
